import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { FileText, Loader2, ScrollText, Upload } from "lucide-react";
import { Navigate, useParams } from "react-router-dom";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { useAuth } from "@/contexts/AuthContext";
import { pageActionButtonClassName, pageActionLabelClassName, pageDescriptionMobileHiddenClassName } from "@/lib/page-action-button";
import {
  canDeleteKmDocument,
  canRegenerateKmDocument,
  canUpdateKmLibrary,
  canUploadKmDocument,
  canViewKmDocument,
} from "../lib/km-org-permissions.js";
import {
  Button,
} from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  deleteDocument,
  getDocumentDownloadUrl,
  getDocumentProcessingStatus,
  getLibrary,
  getWorkspace,
  listDocuments,
  pollDocumentUntilDone,
  reprocessDocument,
  uploadDocument,
} from "../api/rag-api.js";
import { mapDocument, mapLibrary, mapWorkspace } from "../api/mappers.js";
import { isPdfDocument } from "@/apps/rag/lib/document-open.js";
import {
  DocumentViewerPanel,
  type ActiveDocumentView,
} from "../components/DocumentViewerPanel.js";
import { LibraryGuidelinesDialog } from "../components/LibraryGuidelinesDialog.js";
import { ListPagination } from "../components/ListPagination.js";
import {
  RagDocumentsListToolbar,
  type RagDocumentsViewMode,
} from "../components/RagDocumentsListToolbar.js";
import { RagDocumentDetailsSheet } from "../components/RagDocumentDetailsSheet.js";
import { RagDocumentsGrid } from "../components/RagDocumentsGrid.js";
import { RagDocumentsTable } from "../components/RagDocumentsTable.js";
import { AddSourcesDialog } from "../components/AddSourcesDialog.js";
import { RagPageHeader } from "../components/RagPageHeader.js";
import type { RagDocument, RagLibrary, RagWorkspace } from "../types.js";

interface ProcessingStatusDetails {
  title: string;
  processingStatus: string;
  conversionStatus: string | null;
  numPages: number | null;
  numChunks: number | null;
  elapsedSeconds: number | null;
  error: string | null;
}

export function LibraryDocumentsPage() {
  const { workspaceId = "", libraryId = "" } = useParams();
  const { user, hasPermission } = useAuth();
  const canViewDocument = canViewKmDocument(hasPermission);
  const canUploadDocument = canUploadKmDocument(hasPermission);
  const canRegenerateDocument = canRegenerateKmDocument(hasPermission);
  const canDeleteDocument = canDeleteKmDocument(hasPermission);
  const [activeDocument, setActiveDocument] = useState<ActiveDocumentView | null>(null);
  const [workspace, setWorkspace] = useState<RagWorkspace | null>(null);
  const [library, setLibrary] = useState<RagLibrary | null>(null);
  const [documents, setDocuments] = useState<RagDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tableError, setTableError] = useState<string | null>(null);
  const [guidelinesOpen, setGuidelinesOpen] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [statusDialog, setStatusDialog] = useState<ProcessingStatusDetails | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [reprocessTarget, setReprocessTarget] = useState<RagDocument | null>(null);
  const [reprocessing, setReprocessing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [documentsView, setDocumentsView] = useLocalStorage<RagDocumentsViewMode>(
    "rag-library-documents-view-v2",
    "cards"
  );
  const [detailsDocumentId, setDetailsDocumentId] = useState<string | null>(null);

  const detailsDocument = useMemo(
    () => (detailsDocumentId ? documents.find((doc) => doc.id === detailsDocumentId) ?? null : null),
    [detailsDocumentId, documents],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQ(searchQuery.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQ, libraryId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const searchQ = debouncedQ || undefined;
    try {
      const [{ workspace: workspaceData }, { library: libraryData }, listResult] =
        await Promise.all([
          getWorkspace(workspaceId),
          getLibrary(libraryId),
          listDocuments(libraryId, { page, limit, q: searchQ }),
        ]);
      setWorkspace(mapWorkspace(workspaceData));
      setLibrary(mapLibrary(libraryData));
      setDocuments(listResult.documents.map((doc) => mapDocument(doc, libraryId)));
      setTotal(listResult.total);
      const nextTotalPages = Math.max(1, Math.ceil(listResult.total / limit));
      if (page > nextTotalPages) {
        setPage(nextTotalPages);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [workspaceId, libraryId, page, limit, debouncedQ]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSourcesUpload = useCallback(
    async (file: File, title?: string) => {
      const { document } = await uploadDocument(libraryId, file, title);
      await pollDocumentUntilDone(document.id, { maxAttempts: 1 }).catch(() => undefined);
      if (page === 1) {
        await load();
      } else {
        setPage(1);
      }
    },
    [libraryId, page, load]
  );

  useEffect(() => {
    const hasProcessing = documents.some(
      (doc) => doc.status === "processing" || doc.status === "pending"
    );
    if (!hasProcessing) return;

    const interval = window.setInterval(() => {
      void load();
    }, 5000);

    return () => window.clearInterval(interval);
  }, [documents, load]);

  async function handleView(documentId: string) {
    setTableError(null);
    const document = documents.find((doc) => doc.id === documentId);
    if (!document) return;

    try {
      if (document.type === "PDF" || isPdfDocument(null, document.title)) {
        setActiveDocument({
          documentId,
          title: document.title,
        });
        return;
      }

      const { downloadUrl } = await getDocumentDownloadUrl(documentId);
      window.open(downloadUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      setTableError(err instanceof Error ? err.message : "Failed to open document");
    }
  }

  async function handleDelete(documentId: string) {
    setTableError(null);
    const document = documents.find((doc) => doc.id === documentId);
    if (!document) return;

    const confirmed = window.confirm(`Delete "${document.title}"? This cannot be undone.`);
    if (!confirmed) return;

    try {
      await deleteDocument(documentId);
      await load();
    } catch (err) {
      setTableError(err instanceof Error ? err.message : "Failed to delete document");
    }
  }

  function handleReprocess(documentId: string) {
    setTableError(null);
    const document = documents.find((doc) => doc.id === documentId);
    if (!document) return;
    setReprocessTarget(document);
  }

  async function confirmReprocess() {
    if (!reprocessTarget) return;

    setReprocessing(true);
    setTableError(null);
    try {
      await reprocessDocument(reprocessTarget.id);
      await load();
    } catch (err) {
      setTableError(err instanceof Error ? err.message : "Failed to reprocess document");
    } finally {
      setReprocessTarget(null);
      setReprocessing(false);
    }
  }

  async function handleStatus(documentId: string) {
    setTableError(null);
    const document = documents.find((doc) => doc.id === documentId);
    if (!document) return;

    setStatusLoading(true);
    try {
      const status = await getDocumentProcessingStatus(documentId);
      setStatusDialog({
        title: document.title,
        processingStatus: status.processingStatus,
        conversionStatus: status.conversionStatus,
        numPages: status.numPages,
        numChunks: status.numChunks,
        elapsedSeconds: status.elapsedSeconds,
        error: status.error,
      });
    } catch (err) {
      setTableError(err instanceof Error ? err.message : "Failed to load document status");
    } finally {
      setStatusLoading(false);
    }
  }

  if (loading && !library) {
    return (
      <section className="flex flex-1 items-center justify-center p-6">
        <Loader2 className="h-8 w-8 animate-spin text-violet-400" />
      </section>
    );
  }

  if (error || !workspace || !library) {
    return <Navigate to="/rag" replace />;
  }

  const canUpdateLibrary = canUpdateKmLibrary(hasPermission, user?.id, workspace);

  return (
    <section className="flex h-[calc(100dvh-3rem)] min-h-0 flex-1 flex-col overflow-hidden p-6">
      <RagPageHeader
          icon={FileText}
          title="Documents"
          subtitle={`${library.name} · ${total} documents`}
          mobileActions={[
            ...(canUploadDocument
              ? [
                  {
                    id: "add-sources",
                    icon: Upload,
                    label: "Upload files",
                    onClick: () => setSourcesOpen(true),
                  },
                ]
              : []),
            ...(canUpdateLibrary
              ? [
                  {
                    id: "library-guidelines",
                    icon: ScrollText,
                    label: "Library guidelines",
                    onClick: () => setGuidelinesOpen(true),
                  },
                ]
              : []),
          ]}
          action={
            canUpdateLibrary || canUploadDocument ? (
              <>
                {canUploadDocument ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setSourcesOpen(true)}
                    title="Upload files"
                    aria-label="Upload files"
                    className={pageActionButtonClassName}
                  >
                    <Upload className="h-4 w-4" />
                    <span className={pageActionLabelClassName}>Upload files</span>
                  </Button>
                ) : null}
                {canUpdateLibrary ? (
                  <Button
                    variant="outline"
                    onClick={() => setGuidelinesOpen(true)}
                    title="Library guidelines"
                    aria-label="Library guidelines"
                    className={pageActionButtonClassName}
                  >
                    <ScrollText className="h-4 w-4" />
                    <span className={pageActionLabelClassName}>Library guidelines</span>
                  </Button>
                ) : null}
              </>
            ) : undefined
          }
        />
        {tableError ? <p className="mb-3 text-sm text-red-400">{tableError}</p> : null}
        {total > 0 || debouncedQ ? (
          <RagDocumentsListToolbar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            view={documentsView}
            onViewChange={setDocumentsView}
          />
        ) : null}
        <div className="min-h-0 flex-1 overflow-y-auto">
        {documents.length === 0 ? (
          <p className="text-sm text-slate-400">
            {debouncedQ
              ? <>No documents match &ldquo;{debouncedQ}&rdquo;</>
              : "No documents in this library."}
          </p>
        ) : documentsView === "table" ? (
          <RagDocumentsTable
            documents={documents}
            onView={canViewDocument ? handleView : undefined}
            onStatus={canViewDocument ? handleStatus : undefined}
            onReprocess={canRegenerateDocument ? handleReprocess : undefined}
            onDelete={canDeleteDocument ? handleDelete : undefined}
          />
        ) : (
          <RagDocumentsGrid
            documents={documents}
            onDetails={(documentId) => setDetailsDocumentId(documentId)}
            onView={canViewDocument ? handleView : undefined}
            onStatus={canViewDocument ? handleStatus : undefined}
            onReprocess={canRegenerateDocument ? handleReprocess : undefined}
            onDelete={canDeleteDocument ? handleDelete : undefined}
          />
        )}
        </div>
        <RagDocumentDetailsSheet
          open={detailsDocumentId !== null}
          onOpenChange={(open) => {
            if (!open) setDetailsDocumentId(null);
          }}
          document={detailsDocument}
          workspaceName={workspace.name}
          libraryName={library.name}
          onView={canViewDocument ? handleView : undefined}
          onStatus={canViewDocument ? handleStatus : undefined}
        />

        <ListPagination
          className="mt-auto shrink-0"
          page={page}
          limit={limit}
          total={total}
          loading={loading}
          onPageChange={setPage}
          onLimitChange={(nextLimit) => {
            setLimit(nextLimit);
            setPage(1);
          }}
        />

        <Dialog open={statusDialog !== null} onOpenChange={(open) => !open && setStatusDialog(null)}>
        <DialogContent className="border-glass-border bg-[#111b35] text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Processing status</DialogTitle>
            <DialogDescription className={`text-slate-400 ${pageDescriptionMobileHiddenClassName}`}>
              {statusDialog?.title}
            </DialogDescription>
          </DialogHeader>
          {statusDialog ? (
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-slate-400">Status</dt>
                <dd className="text-white">{statusDialog.processingStatus}</dd>
              </div>
              {statusDialog.conversionStatus ? (
                <div className="flex justify-between gap-4">
                  <dt className="text-slate-400">Conversion</dt>
                  <dd className="text-white">{statusDialog.conversionStatus}</dd>
                </div>
              ) : null}
              {statusDialog.numPages != null ? (
                <div className="flex justify-between gap-4">
                  <dt className="text-slate-400">Pages</dt>
                  <dd className="text-white">{statusDialog.numPages}</dd>
                </div>
              ) : null}
              {statusDialog.numChunks != null ? (
                <div className="flex justify-between gap-4">
                  <dt className="text-slate-400">Chunks</dt>
                  <dd className="text-white">{statusDialog.numChunks}</dd>
                </div>
              ) : null}
              {statusDialog.elapsedSeconds != null ? (
                <div className="flex justify-between gap-4">
                  <dt className="text-slate-400">Elapsed</dt>
                  <dd className="text-white">{statusDialog.elapsedSeconds}s</dd>
                </div>
              ) : null}
              {statusDialog.error ? (
                <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-red-400">
                  {statusDialog.error}
                </div>
              ) : null}
            </dl>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialog(null)} disabled={statusLoading}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {activeDocument ? (
        <DocumentViewerPanel
          document={activeDocument}
          onClose={() => setActiveDocument(null)}
        />
      ) : null}
      <LibraryGuidelinesDialog
        open={guidelinesOpen}
        onOpenChange={setGuidelinesOpen}
        library={{ id: library.id, name: library.name }}
      />
      {canUploadDocument ? (
        <AddSourcesDialog
          open={sourcesOpen}
          onOpenChange={setSourcesOpen}
          onUpload={handleSourcesUpload}
        />
      ) : null}
      <ConfirmDialog
        isOpen={reprocessTarget !== null}
        onClose={() => setReprocessTarget(null)}
        onConfirm={confirmReprocess}
        title="Reprocess document"
        description={
          reprocessTarget ? `Reprocess "${reprocessTarget.title}"?` : ""
        }
        confirmLabel="Reprocess"
        isLoading={reprocessing}
      />
    </section>
  );
}
